import { Box, Button, Flex, Select, Text, TextField } from '@radix-ui/themes';
import React, { useState, useEffect, useCallback, useRef } from 'react';

const defaultFilters = {
  prn: '',
  alarmType: 'all',
  dateFrom: '',
  dateTo: '',
  timeFrom: '',
  timeTo: '',
  location: '',
  status: 'all',
};

const HistoryFilter = ({
  filters,
  onFilterChange,
  onReset,
  alarmOptions = [],
  statusOptions = [],
}) => {
  // Local state for text inputs to prevent cursor loss on re-render
  const [localPrn, setLocalPrn] = useState(filters.prn);
  const [localLocation, setLocalLocation] = useState(filters.location);

  // Sync local state when filters are reset externally
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (prev.prn !== filters.prn && filters.prn !== localPrn) {
      setLocalPrn(filters.prn);
    }
    if (prev.location !== filters.location && filters.location !== localLocation) {
      setLocalLocation(filters.location);
    }
    prevFiltersRef.current = filters;
  }, [filters]);

  // Debounced propagation of text input changes to parent
  const debounceRef = useRef(null);
  const propagateText = useCallback((key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ ...filters, [key]: value });
    }, 300);
  }, [filters, onFilterChange]);

  const handlePrnChange = (e) => {
    const val = e.target.value;
    setLocalPrn(val);
    propagateText('prn', val);
  };

  const handleLocationChange = (e) => {
    const val = e.target.value;
    setLocalLocation(val);
    propagateText('location', val);
  };

  // Immediate update for selects and date/time pickers
  const updateFilter = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => (
    defaultFilters[key] !== value
  ));

  return (
    <Box
      style={{
        border: '1px solid var(--gray-4)',
        borderRadius: '8px',
        backgroundColor: 'var(--color-surface)',
        padding: '14px',
      }}
    >
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center" gap="3">
          <Text size="3" weight="medium">Filters</Text>
          <Button variant="soft" size="2" onClick={onReset} disabled={!hasActiveFilters}>
            Clear filters
          </Button>
        </Flex>

        {/* Row 1: PRN | Alarm Type | Location | Status */}
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
          }}
        >
          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">PRN</Text>
            <TextField.Root
              value={localPrn}
              onChange={handlePrnChange}
              placeholder="Search PRN"
              size="2"
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Alarm Type</Text>
            <Select.Root
              value={filters.alarmType}
              onValueChange={(value) => updateFilter('alarmType', value)}
              size="2"
            >
              <Select.Trigger placeholder="All alarms" style={{ width: '100%' }} />
              <Select.Content>
                <Select.Item value="all">All alarms</Select.Item>
                {alarmOptions.map((alarm) => (
                  <Select.Item key={alarm.value} value={alarm.value}>
                    {alarm.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Location</Text>
            <TextField.Root
              value={localLocation}
              onChange={handleLocationChange}
              placeholder="Search location"
              size="2"
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Status</Text>
            <Select.Root
              value={filters.status}
              onValueChange={(value) => updateFilter('status', value)}
              size="2"
            >
              <Select.Trigger placeholder="All statuses" style={{ width: '100%' }} />
              <Select.Content>
                <Select.Item value="all">All statuses</Select.Item>
                {statusOptions.map((status) => (
                  <Select.Item key={status} value={status}>
                    {status}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Flex>
        </Box>

        {/* Row 2: Date From | Date To | Time From | Time To */}
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
          }}
        >
          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Date From</Text>
            <TextField.Root
              type="date"
              value={filters.dateFrom}
              onChange={(event) => updateFilter('dateFrom', event.target.value)}
              size="2"
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Date To</Text>
            <TextField.Root
              type="date"
              value={filters.dateTo}
              onChange={(event) => updateFilter('dateTo', event.target.value)}
              size="2"
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Time From</Text>
            <TextField.Root
              type="time"
              value={filters.timeFrom}
              onChange={(event) => updateFilter('timeFrom', event.target.value)}
              size="2"
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text as="div" size="2" weight="medium">Time To</Text>
            <TextField.Root
              type="time"
              value={filters.timeTo}
              onChange={(event) => updateFilter('timeTo', event.target.value)}
              size="2"
            />
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export { defaultFilters as defaultHistoryFilters };
export default HistoryFilter;
